'use client';

import { useIsAppUser } from '@ps/functions/hooks/useIsAppUser';
import { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';
import { useIsHydrated } from '@ps/ui/hooks/useIsHydrated';
import { CircleDollarSign, Lock } from 'lucide-react';

import { SubscriptionLevelBadge } from '../../../../subscription/components/SubscriptionLevelBadge';
import type { PostGatingViewModel } from '../../types';
import { TipProgress } from './TipProgress';
import { UnlockButton } from './UnlockButton';

interface Props {
  gating: PostGatingViewModel;
  /** Shrinks the lock + CTAs for tight surfaces (the chat-bubble post card). */
  compact?: boolean;
  /**
   * Unlock actions. When omitted (a preview surface like the chat card) the CTAs
   * stay inert so the tap falls through to the host (the card opens the modal);
   * the real unlock flows are wired on the full post view via {@link usePostUnlock}.
   */
  onSubscribe?: (level: PopStoreExtendedSubscriptionLevel | undefined) => void;
  onPay?: () => void;
  onTip?: () => void;
}

/**
 * The locked-content overlay — a transparent, absolutely-filled layer with a lock
 * icon and the unlock CTA(s) for whichever gates the post has. 1:1 with legacy
 * `PostLockedView` (`flows/bio/wall/components/post/locked.tsx`). It sits over the
 * gated slide's blurhash preview. Pure: the decision of WHEN to show it
 * (restricted + not owner-exempt, active slide past the free window) and the
 * unlock actions belong to the caller.
 */
export function PostPaywall({
  gating,
  compact,
  onSubscribe,
  onPay,
  onTip,
}: Props) {
  const { subscribe, pay, tip } = gating;
  // Inside the CommentSold native app, paywall CTAs are hidden — payment must
  // route through IAP (App Store compliance). Show the lock icon +
  // any read-only tip-goal metadata; hide only the unlock CTA buttons.
  // Matches legacy `flows/bio/wall/components/post/locked.tsx:138`.
  const isAppUser = useIsAppUser();
  // Once the post is in pay-to-view-only mode, a subscription (even a higher
  // tier) can't unlock it — only a purchase can — so the subscribe CTA would
  // dead-end. Decide "pay-only right now" here on the client: `payToViewOnlyFrom`
  // is a fixed instant from the VM, compared against the current time only after
  // hydration (never a server clock, and no SSR/hydration mismatch — it reads
  // `false` on the server and first client render, then re-evaluates on mount).
  const hydrated = useIsHydrated();
  const payToViewOnlyNow =
    gating.payToViewOnlyFrom != null &&
    hydrated &&
    Date.now() >= gating.payToViewOnlyFrom;
  return (
    <div
      className={`absolute inset-0 flex flex-col items-center justify-center text-center ${compact ? 'gap-2 px-2.5' : 'gap-4 px-6'}`}
    >
      <Lock
        className={`text-raise/90 drop-shadow ${compact ? 'h-8 w-8' : 'h-14 w-14'}`}
        strokeWidth={1.5}
        aria-hidden
      />
      <div
        className={`flex flex-col items-stretch ${compact ? 'w-fit gap-1.5' : 'w-full max-w-[300px] gap-2'}`}
      >
        {subscribe && !isAppUser && !payToViewOnlyNow ? (
          <UnlockButton
            variant="primary"
            compact={compact}
            onClick={
              onSubscribe ? () => onSubscribe(subscribe.level) : undefined
            }
          >
            {subscribe.level === PopStoreExtendedSubscriptionLevel.FOLLOWER ? (
              'Subscribe Free to Unlock'
            ) : (
              <>
                <SubscriptionLevelBadge level={subscribe.level} />
                Subscribe to Unlock
              </>
            )}
          </UnlockButton>
        ) : null}

        {pay && !isAppUser ? (
          <UnlockButton variant="secondary" compact={compact} onClick={onPay}>
            Unlock for {pay.price}
            {pay.originalPrice ? (
              <span className="text-sink/35 ml-1 font-normal line-through">
                {pay.originalPrice}
              </span>
            ) : null}
          </UnlockButton>
        ) : null}

        {tip ? (
          <div className="flex w-full flex-col items-stretch gap-2">
            <span
              className={`text-content-1 font-semibold drop-shadow ${compact ? 'text-[11px]' : 'text-[13px]'}`}
            >
              TIP GOAL: {tip.goalLabel}
            </span>
            <TipProgress
              raisedLabel={tip.raisedLabel}
              progress={tip.progress}
              compact={compact}
            />
            {isAppUser ? null : (
              <UnlockButton variant="primary" compact={compact} onClick={onTip}>
                <CircleDollarSign className="h-4 w-4" aria-hidden />
                Tip to Unlock!
              </UnlockButton>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
